import { useMemo } from 'react'
import type { DashboardStatusCategory } from '@myapp/contract'
import type { DashboardMetric } from '../../../entities/dashboard'
import {
  dashboardMonthOptions,
  dashboardYearOptions,
  periodMonths,
  sanitizeDashboardFilters,
  toDashboardApiParams,
} from './dashboardFilters.logic'
import type { DashboardPeriodMode } from './dashboardFilters.types'
import { toDashboardFiltersForm, useDashboardFiltersParams } from './useDashboardFiltersParams'

/** A chart click selects that one entry; clicking it again clears the selection. */
const toggleSingle = (current: readonly string[], key: string): string[] =>
  current.length === 1 && current[0] === key ? [] : [key]

export function useDashboardFilters() {
  const [params, setParams] = useDashboardFiltersParams()

  const filters = useMemo(
    () => sanitizeDashboardFilters(toDashboardFiltersForm(params), new Date()),
    [params],
  )
  const apiParams = useMemo(() => toDashboardApiParams(filters), [filters])
  const months = useMemo(() => periodMonths(filters), [filters])
  const yearOptions = useMemo(() => dashboardYearOptions(new Date()), [])
  const monthOptions = useMemo(() => dashboardMonthOptions(new Date()), [])

  const actions = useMemo(
    () => ({
      setMode: (mode: DashboardPeriodMode) => void setParams({ mode }),
      setYear: (year: number) => void setParams({ year }),
      // Von and Bis never cross: moving one past the other drags it along.
      setFrom: (from: string) =>
        void setParams({ from, to: filters.to < from ? from : filters.to }),
      setTo: (to: string) =>
        void setParams({ to, from: filters.from > to ? to : filters.from }),
      setTechnicianId: (technicianId: number | null) => void setParams({ technicianId }),
      setGewerke: (gewerke: string[]) => void setParams({ gewerke }),
      toggleGewerk: (key: string) => void setParams({ gewerke: toggleSingle(filters.gewerke, key) }),
      removeGewerk: (key: string) =>
        void setParams({ gewerke: filters.gewerke.filter((g) => g !== key) }),
      toggleStatus: (status: DashboardStatusCategory) =>
        void setParams({ status: filters.status === status ? null : status }),
      clearStatus: () => void setParams({ status: null }),
      setMetric: (metric: DashboardMetric) => void setParams({ metric }),
    }),
    [filters, setParams],
  )

  return { filters, apiParams, months, yearOptions, monthOptions, actions }
}

export type DashboardFiltersActions = ReturnType<typeof useDashboardFilters>['actions']
